import { useCallback, useEffect, useRef, useState } from 'react';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import {
  ComposerDocument,
  ComposerParts,
  ComposerValidatorContext,
  ValidatorErrorWithId,
  ValidatorErrorWithIndex,
} from '../types';
import { validateComposerDocument } from '../utils/validate-composer-document';

type Props = {
  enableValidation: boolean;
  getValue: () => ComposerDocument | '';
  isInitialized: boolean;
  locationIds: string[];
  parts: ComposerParts;
  selectedLocationId: string;
  brandSettings?: ComposerValidatorContext['brandSettings'];
};

export const useValidation = ({
  enableValidation,
  getValue,
  isInitialized,
  locationIds,
  parts,
  selectedLocationId,
  brandSettings,
}: Props) => {
  const [inFlightValidations, setInFlightValidations] = useState(0);
  const [validationErrors, setValidationErrors] = useState<ValidatorErrorWithId[]>([]);
  const fullValidationRunId = useRef(0);
  const sectionValidationRunIds = useRef<Record<string, number>>({});
  const validationRunCounter = useRef(0);
  const hasValidated = fullValidationRunId.current > 0;
  const { query } = useEditor();

  const getSectionIds = useCallback(() => {
    const root = query.node(ROOT_NODE);
    return [...root.childNodes(), ...root.linkedNodes()];
  }, [query]);

  const mapValidationResultToSectionErrors = useCallback((result: ValidatorErrorWithIndex[], sectionIds: string[]) => {
    return result.reduce<ValidatorErrorWithId[]>((acc, error) => {
      const sectionId = sectionIds[error.sectionIndex];
      if (!sectionId) return acc;

      acc.push({
        code: error.code,
        count: error.count,
        sectionId,
      });

      return acc;
    }, []);
  }, []);

  const startValidation = useCallback(() => {
    setInFlightValidations((count) => count + 1);
  }, []);

  const endValidation = useCallback(() => {
    setInFlightValidations((count) => Math.max(0, count - 1));
  }, []);

  const clearSectionValidation = useCallback((sectionId: string) => {
    if (!sectionId) return;

    validationRunCounter.current += 1;
    sectionValidationRunIds.current[sectionId] = validationRunCounter.current;
    setValidationErrors((errors) => errors.filter((error) => error.sectionId !== sectionId));
  }, []);

  const validate = useCallback(async () => {
    const document = getValue();
    if (!document) {
      setValidationErrors([]);
      return [];
    }

    validationRunCounter.current += 1;
    const runId = validationRunCounter.current;
    fullValidationRunId.current = runId;
    startValidation();

    try {
      const result = await validateComposerDocument(document, parts, {
        brandSettings,
        locationIds,
        selectedLocationId,
      });

      if (fullValidationRunId.current === runId) {
        const sectionIds = getSectionIds();
        const composerValidationErrors = mapValidationResultToSectionErrors(result, sectionIds);

        // Stamp all known section ids to invalidate stale section-only validations that started earlier
        validationRunCounter.current += 1;
        const sectionRunId = validationRunCounter.current;
        sectionIds.forEach((sectionId) => {
          sectionValidationRunIds.current[sectionId] = sectionRunId;
        });

        setValidationErrors(composerValidationErrors);
      }

      return result;
    } finally {
      endValidation();
    }
  }, [
    brandSettings,
    endValidation,
    getSectionIds,
    getValue,
    locationIds,
    mapValidationResultToSectionErrors,
    parts,
    selectedLocationId,
    startValidation,
  ]);

  const validateSection = useCallback(
    async (sectionId: string) => {
      if (!sectionId) return [];

      const document = getValue();
      if (!document) {
        clearSectionValidation(sectionId);
        return [];
      }

      const sectionIds = getSectionIds();
      const sectionIndex = sectionIds.indexOf(sectionId);
      if (sectionIndex < 0) {
        clearSectionValidation(sectionId);
        return [];
      }

      validationRunCounter.current += 1;
      const runId = validationRunCounter.current;
      sectionValidationRunIds.current[sectionId] = runId;
      const fullRunIdAtStart = fullValidationRunId.current;
      startValidation();

      try {
        const result = await validateComposerDocument(
          document,
          parts,
          {
            brandSettings,
            locationIds,
            selectedLocationId,
          },
          {
            sectionIndexes: [sectionIndex],
          }
        );

        const latestSectionRunId = sectionValidationRunIds.current[sectionId];
        const hasNewerFullValidation = fullValidationRunId.current > fullRunIdAtStart;

        if (latestSectionRunId === runId && !hasNewerFullValidation) {
          const sectionErrors = mapValidationResultToSectionErrors(result, sectionIds).filter(
            (error) => error.sectionId === sectionId
          );

          setValidationErrors((errors) => {
            const otherSections = errors.filter((error) => error.sectionId !== sectionId);
            return [...otherSections, ...sectionErrors];
          });
        }

        return result;
      } finally {
        endValidation();
      }
    },
    [
      brandSettings,
      clearSectionValidation,
      endValidation,
      getSectionIds,
      getValue,
      locationIds,
      mapValidationResultToSectionErrors,
      parts,
      selectedLocationId,
      startValidation,
    ]
  );

  useEffect(() => {
    if (!enableValidation || !isInitialized || fullValidationRunId.current > 0) return;
    void validate();
  }, [enableValidation, isInitialized, validate]);

  return {
    clearSectionValidation,
    hasValidated,
    isValidating: inFlightValidations > 0,
    validate,
    validateSection,
    validationErrors,
  };
};
